import React, { useState } from 'react'
import { CheckIcon } from '@heroicons/react/24/outline'
import { formatPhone, onlyDigits } from './dashboardHelpers'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

type ContactPhoneInputProps = {
  label: React.ReactNode
  value: string
  onChange: (value: string) => void
  suggestions?: string[]
  disabled?: boolean
  required?: boolean
  placeholder?: string
  helpText?: string
}

function uniquePhones(phones: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of phones) {
    const digits = onlyDigits(raw)
    if (!digits || seen.has(digits)) continue
    seen.add(digits)
    out.push(digits)
  }
  return out
}

export const ContactPhoneInput: React.FC<ContactPhoneInputProps> = ({
  label,
  value,
  onChange,
  suggestions = [],
  disabled,
  required,
  placeholder = '(00) 00000-0000',
  helpText,
}) => {
  const [hint, setHint] = useState<string | null>(null)
  const selectedDigits = onlyDigits(value)
  const known = uniquePhones(suggestions)

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required ? <span className="text-red-500"> *</span> : null}
      </label>

      {known.length > 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3 space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Já cadastrados — clique para selecionar
          </p>
          <div className="flex flex-wrap gap-2">
            {known.map((digits) => {
              const active = selectedDigits === digits
              return (
                <button
                  key={digits}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return
                    onChange(active ? '' : formatPhone(digits))
                    setHint(null)
                  }}
                  className={[
                    'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm border transition-colors',
                    active
                      ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                      : 'bg-white text-gray-800 border-gray-300 hover:border-primary-400 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-600',
                    disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                  ].join(' ')}
                  aria-pressed={active}
                >
                  {active ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : null}
                  {formatPhone(digits)}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      <input
        className={inputClass}
        value={formatPhone(value)}
        disabled={disabled}
        placeholder={placeholder}
        inputMode="tel"
        maxLength={15}
        onChange={(e) => {
          onChange(formatPhone(e.target.value))
          if (hint) setHint(null)
        }}
        onBlur={() => {
          const digits = onlyDigits(value)
          if (!digits) return
          if (digits.length < 10) {
            setHint('Informe um telefone válido (DDD + número).')
            return
          }
          onChange(formatPhone(digits))
        }}
      />

      {hint ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{hint}</p>
      ) : helpText ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      ) : null}
    </div>
  )
}
